/**
 * API Client for Vacation Tracker
 *
 * Uses absolute API URL for cross-origin requests from workspace subdomains
 */

const API_URL = import.meta.env.VITE_API_URL || 'https://api.unplug-app.com';
// Use /v3/ prefix to bypass any stale cached /api/ responses
const BASE_URL = `${API_URL}/v3`;

function getAuthHeaders() {
  const sessionId = localStorage.getItem('sessionId');
  return {
    'Content-Type': 'application/json',
    'Authorization': sessionId ? `Bearer ${sessionId}` : ''
  };
}

async function request(method, path, body = null) {
  const options = {
    method,
    headers: getAuthHeaders(),
    credentials: 'include',  // Include cookies for cross-origin requests
    cache: 'no-store'  // Bypass browser cache to avoid stale CORS headers
  };

  if (body) {
    options.body = JSON.stringify(body);
  }

  // Add cache-busting parameter to force new preflight requests
  const separator = path.includes('?') ? '&' : '?';
  const url = `${BASE_URL}${path}${separator}_t=${Date.now()}`;

  try {
    const response = await fetch(url, options);
    const text = await response.text();

    let data;
    try {
      data = text ? JSON.parse(text) : {};
    } catch (parseErr) {
      // Never log full headers or bodies - they contain the session token
      // and personal data (visible to anything reading the console)
      console.error('[API Parse Error]', method, path, response.status, parseErr.message);
      throw new Error('Invalid JSON response from server');
    }

    if (!response.ok) {
      // Prefer detailed message over short error code
      throw new Error(data.message || data.error || 'Request failed');
    }

    return data;
  } catch (err) {
    console.error('[API Error]', err.name, err.message);
    throw err;
  }
}

// Public API call (no auth required) - used for workspace lookup by slug
// Uses /v3/ prefix to bypass stale cached responses
export async function getWorkspaceBySlug(slug) {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout

  try {
    console.log('[API] Fetching workspace by slug:', slug);
    const response = await fetch(`${API_URL}/v3/workspace/by-slug/${slug}?_t=${Date.now()}`, {
      credentials: 'include',
      cache: 'no-store',
      signal: controller.signal
    });
    clearTimeout(timeoutId);
    console.log('[API] Workspace fetch response status:', response.status);

    if (!response.ok) {
      if (response.status === 404) {
        return null;
      }
      throw new Error('Failed to fetch workspace');
    }
    return response.json();
  } catch (err) {
    clearTimeout(timeoutId);
    if (err.name === 'AbortError') {
      console.error('[API] Workspace fetch timed out');
      throw new Error('Request timed out - please check your connection');
    }
    throw err;
  }
}

// Dashboard
export const logoutSession = () => request('POST', '/auth/logout');

export const getDashboardStats = () => request('GET', '/dashboard');

// Users
export const getUsers = (status = 'active') => request('GET', `/users?status=${status}`);
export const getAllUsers = () => request('GET', '/users?status=all');
export const getUser = (id) => request('GET', `/users/${id}`);
export const updateUser = (id, data) => request('PUT', `/users/${id}`, data);
export const activateUser = (id) => request('PUT', `/users/${id}`, { is_active: true, is_deleted: false });
export const deactivateUser = (id) => request('PUT', `/users/${id}`, { is_active: false });
export const deleteUser = (id) => request('PUT', `/users/${id}`, { is_deleted: true });
export const restoreUser = (id) => request('PUT', `/users/${id}`, { is_deleted: false, is_active: true });
export const permanentlyDeleteUser = (id) => request('DELETE', `/users/${id}`);

// Leave Types
export const getLeaveTypes = () => request('GET', '/leave-types');
export const createLeaveType = (data) => request('POST', '/leave-types', data);
export const updateLeaveType = (id, data) => request('PUT', `/leave-types/${id}`, data);
export const deleteLeaveType = (id) => request('DELETE', `/leave-types/${id}`);

// Leave Requests
export const getLeaves = (params = {}) => {
  const query = new URLSearchParams(params).toString();
  return request('GET', `/leaves${query ? `?${query}` : ''}`);
};
export const createLeave = (data) => request('POST', '/leaves', data);
export const updateLeave = (id, data) => request('PUT', `/leaves/${id}`, data);
export const approveLeave = (id) => request('POST', `/leaves/${id}/approve`);
export const rejectLeave = (id, reason) => request('POST', `/leaves/${id}/reject`, { reason });
export const cancelLeave = (id) => request('DELETE', `/leaves/${id}`);

// Balances
export const getBalances = (params = {}) => {
  const query = new URLSearchParams(params).toString();
  return request('GET', `/balances${query ? `?${query}` : ''}`);
};
export const getAllUserBalances = (year) => request('GET', `/balances/all?year=${year}`);
export const updateUserBalance = (userId, leaveTypeId, year, data) =>
  request('PUT', `/balances/${userId}/${leaveTypeId}/${year}`, data);
export const initialiseBalances = (year) => request('POST', '/balances/initialise', { year });
export const recalculateBalances = (year) => request('POST', '/balances/recalculate', { year });
export const getUserBalances = (userId, year) => getBalances({ user_id: userId, year });

// Departments
export const getDepartments = () => request('GET', '/departments');
export const createDepartment = (data) => request('POST', '/departments', data);
export const updateDepartment = (id, data) => request('PUT', `/departments/${id}`, data);
export const deleteDepartment = (id) => request('DELETE', `/departments/${id}`);
export const getDepartmentPolicies = (id) => request('GET', `/departments/${id}/policies`);
export const updateDepartmentPolicies = (id, policies) => request('PUT', `/departments/${id}/policies`, policies);

// Locations
export const getLocations = () => request('GET', '/locations');
export const createLocation = (data) => request('POST', '/locations', data);
export const updateLocation = (id, data) => request('PUT', `/locations/${id}`, data);

// Holidays
export const getHolidays = (year) => request('GET', `/holidays?year=${year}`);
export const createHoliday = (data) => request('POST', '/holidays', data);
export const updateHoliday = (id, data) => request('PUT', `/holidays/${id}`, data);
export const deleteHoliday = (id) => request('DELETE', `/holidays/${id}`);

// Birthdays
export const getUpcomingBirthdays = () => request('GET', '/birthdays/upcoming');

// Calendar
export const getCalendarData = (start, end) =>
  request('GET', `/calendar?start=${start}&end=${end}`);

// User Calendar Connections (personal Google/Microsoft calendar linking)
export const getCalendarConnections = () => request('GET', '/calendar/connections');
export const connectCalendar = (provider) => request('POST', `/calendar/connect/${provider}`);
export const disconnectCalendar = (provider) => request('DELETE', `/calendar/disconnect/${provider}`);
export const syncAllCalendarEvents = () => request('POST', '/calendar/sync-all');
export const syncTeamCalendarEvents = () => request('POST', '/calendar/sync-team');
export const setTeamSyncPreference = (provider, enabled) => request('PUT', `/calendar/team-sync/${provider}`, { enabled });

// Notifications
export const getNotificationSettings = () => request('GET', '/notifications');
export const updateNotificationSettings = (data) => request('PUT', '/notifications', data);
export const getSlackChannels = () => request('GET', '/slack/channels');

// TOIL (Time Off In Lieu)
export const getToil = (userId) => request('GET', `/toil?user_id=${userId}`);
export const addToil = (data) => request('POST', '/toil', data);

// Workspace Settings
export const getWorkspaceSettings = () => request('GET', '/workspace-settings');
export const updateWorkspaceSettings = (data) => request('PUT', '/workspace-settings', data);

// Slack User Import
export const searchSlackUsers = (query = '') => request('GET', `/slack/users/search?q=${encodeURIComponent(query)}`);
export const importSlackUser = (slackUserId) => request('POST', '/users/import', { slack_user_id: slackUserId });

// Admin
export const syncUsers = () => request('POST', '/admin/sync-users');
export const getSlackMemberCounts = () => request('GET', '/admin/slack-member-counts');
export const bulkImportFromSlack = (guestFilter = 'all') => request('POST', '/admin/bulk-import', { guestFilter });

// Data Management
export const getDataStats = () => request('GET', '/admin/data-stats');
export const downloadBackup = async () => {
  const sessionId = localStorage.getItem('sessionId');
  const response = await fetch(`${BASE_URL}/admin/backup`, {
    headers: {
      'Authorization': sessionId ? `Bearer ${sessionId}` : ''
    },
    credentials: 'include',
    cache: 'no-store'
  });
  if (!response.ok) throw new Error('Failed to download backup');
  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `vacation-tracker-backup-${new Date().toISOString().split('T')[0]}.json`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
};
export const clearNonAdminUsers = () => request('DELETE', '/admin/clear-users');
export const clearLeaveHistory = () => request('DELETE', '/admin/clear-leave-history');

// Audit Logs
export const getAuditLogs = (params = {}) => {
  const query = new URLSearchParams(params).toString();
  return request('GET', `/audit-logs${query ? `?${query}` : ''}`);
};
export const deleteAuditLogs = (ids) => request('DELETE', '/audit-logs', { ids });

// Leave Instances (for per-event tracking like maternity)
export const getLeaveInstances = (params = {}) => {
  const query = new URLSearchParams(params).toString();
  return request('GET', `/leave-instances${query ? `?${query}` : ''}`);
};
export const createLeaveInstance = (data) => request('POST', '/leave-instances', data);
export const updateLeaveInstance = (id, data) => request('PUT', `/leave-instances/${id}`, data);
export const closeLeaveInstance = (id) => request('PUT', `/leave-instances/${id}`, { status: 'completed' });
export const cancelLeaveInstance = (id) => request('PUT', `/leave-instances/${id}`, { status: 'cancelled' });

// Import from external sources
export const getImportLogs = () => request('GET', '/import/logs');
export const fetchVTData = (apiKey) => request('POST', '/import/vacationtracker/fetch', { apiKey });
export const executeVTImport = (apiKey, mappings) => request('POST', '/import/vacationtracker/execute', { apiKey, mappings });

// Integrations
export const getIntegrations = () => request('GET', '/integrations');
export const getIntegration = (provider) => request('GET', `/integrations/${provider}`);
export const connectIntegration = (provider, data = {}) => request('POST', `/integrations/${provider}/connect`, data);
export const disconnectIntegration = (provider) => request('POST', `/integrations/${provider}/disconnect`);
export const syncIntegration = (provider) => request('POST', `/integrations/${provider}/sync`);
export const getIntegrationLogs = (provider, params = {}) => {
  const query = new URLSearchParams(params).toString();
  return request('GET', `/integrations/${provider}/logs${query ? `?${query}` : ''}`);
};
export const updateIntegrationConfig = (provider, config) => request('PUT', `/integrations/${provider}/config`, { config });

// BambooHR Integration
export const getBambooHRPreview = () => request('GET', '/integrations/bamboohr/preview');
export const getBambooHRMappings = () => request('GET', '/integrations/bamboohr/mappings');
export const saveBambooHRMappings = (mappings) => request('PUT', '/integrations/bamboohr/mappings', mappings);
export const executeBambooHRImport = () => request('POST', '/integrations/bamboohr/import');

// Global Compliance
export const getCountryComplianceProfiles = () => request('GET', '/compliance/countries');
export const getLeaveTypeTemplates = (countryCode) => request('GET', `/compliance/templates/${countryCode}`);

// Workspace Compliance Settings
export const getWorkspaceCompliance = () => request('GET', '/workspace/compliance');
export const updateWorkspaceCompliance = (data) => request('PUT', '/workspace/compliance', data);

// Bereavement Tiers
export const getBereavementTiers = (leaveTypeId) => request('GET', `/bereavement-tiers/${leaveTypeId}`);
export const updateBereavementTiers = (leaveTypeId, tiers) => request('PUT', `/bereavement-tiers/${leaveTypeId}`, { tiers });

// Leave Encashments
export const getEncashments = (params = {}) => {
  const query = new URLSearchParams(params).toString();
  return request('GET', `/encashments${query ? `?${query}` : ''}`);
};
export const requestEncashment = (data) => request('POST', '/encashments', data);
export const approveEncashment = (id) => request('POST', `/encashments/${id}/approve`);
export const rejectEncashment = (id) => request('POST', `/encashments/${id}/reject`);

// Onboarding Tour
export const markTourComplete = () => request('POST', '/tour/complete');
export const resetTour = () => request('POST', '/tour/reset');

// Google Drive Backup (Business tier)
export const getGoogleDriveBackup = () => request('GET', '/backup/google-drive');
export const connectGoogleDrive = () => request('POST', '/backup/google-drive/connect');
export const getGoogleDriveFolders = () => request('GET', '/backup/google-drive/folders');
export const updateGoogleDriveSettings = (settings) => request('PUT', '/backup/google-drive/settings', settings);
export const runGoogleDriveBackup = () => request('POST', '/backup/google-drive/run');
export const disconnectGoogleDrive = () => request('POST', '/backup/google-drive/disconnect');

// Restore from backup (owner only)
export const restoreFromBackup = (backup, confirmCode) => request('POST', '/admin/restore', { backup, confirmCode });

// User Preferences
export const getMyProfile = () => request('GET', '/me');
export const updateMyPreferences = (preferences) => request('PUT', '/me/preferences', preferences);
